import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState("polling");
  const [details, setDetails] = useState(null);
  const attempts = useRef(0);
  const { loadCart } = useApp();

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("failed"); return; }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setDetails(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          loadCart();
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setStatus("failed");
          return;
        }
      } catch (e) {
        // continue polling
      }
      if (attempts.current > 20) { setStatus("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [params, loadCart]);

  return (
    <div className="max-w-[700px] mx-auto px-6 py-32 text-center">
      {status === "polling" && (
        <>
          <Loader2 className="w-10 h-10 mx-auto animate-spin text-[#FF3B30]" />
          <h1 className="font-display font-black text-4xl tracking-tighter mt-6">Confirming your order…</h1>
          <p className="font-mono-brand text-xs uppercase tracking-[0.2em] text-neutral-500 mt-2">
            Do not close this window
          </p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="w-12 h-12 mx-auto text-[#FF3B30]" />
          <div className="font-mono-brand text-xs uppercase tracking-[0.3em] text-[#FF3B30] mt-6">/ payment confirmed</div>
          <h1 className="font-display font-black text-5xl tracking-tighter mt-2" data-testid="payment-success-heading">Thank you.</h1>
          <p className="text-neutral-600 mt-4">
            We received <span className="font-mono-brand">${details?.amount?.toFixed?.(2)}</span>. Your bag has been cleared and an order is on its way.
          </p>
          <div className="flex gap-3 justify-center mt-8">
            <Link to="/orders" className="border border-black px-6 py-3 font-mono-brand text-xs uppercase tracking-[0.2em] hover:bg-black hover:text-white transition-colors" data-testid="view-orders-btn">
              View orders
            </Link>
            <Link to="/shop/all" className="bg-black text-white px-6 py-3 font-mono-brand text-xs uppercase tracking-[0.2em] hover:bg-[#FF3B30] transition-colors">
              Keep shopping →
            </Link>
          </div>
        </>
      )}
      {(status === "failed" || status === "timeout") && (
        <>
          <XCircle className="w-10 h-10 mx-auto text-neutral-500" />
          <h1 className="font-display font-black text-4xl tracking-tighter mt-6">
            {status === "timeout" ? "Still processing." : "Payment not completed."}
          </h1>
          <p className="text-neutral-600 mt-2">
            {status === "timeout" ? "It's taking longer than usual. Check your orders shortly." : "Please try again or contact support."}
          </p>
          <Link to="/cart" className="inline-block mt-6 border border-black px-6 py-3 font-mono-brand text-xs uppercase tracking-[0.2em] hover:bg-black hover:text-white transition-colors">
            Back to cart
          </Link>
        </>
      )}
    </div>
  );
}
